import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ChevronDown, Instagram, Menu, Search, ShoppingBag, User, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { FASHION_SUBS, FURNISHING_SUBS, slugify } from "@/lib/categories";
import { api, inr } from "@/lib/api";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const INSTAGRAM_URL = process.env.REACT_APP_INSTAGRAM_URL || "https://instagram.com/hunardesigns";

const menuGroups = [
  { id: "fashion", label: "Fashion Wear", section: "fashion", subs: FASHION_SUBS },
  { id: "furnishing", label: "Home Furnishing", section: "furnishing", subs: FURNISHING_SUBS },
];

function NavDropdown({ group, active, spotlight }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="group relative" onMouseLeave={() => setOpen(false)}>
      <button
        data-testid={`nav-${group.id}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={`flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.18em] transition-colors duration-300 hover:text-olive ${
          active ? "text-obsidian underline underline-offset-8" : "text-soot"
        }`}
      >
        {group.label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-180 ${open ? "rotate-180" : ""}`} />
      </button>
      <div
        className={`absolute left-0 top-full z-50 pt-3 transition-all duration-200 group-hover:visible group-hover:opacity-100 ${
          open ? "visible opacity-100" : "invisible opacity-0"
        }`}
      >
        <div className="flex border border-seam bg-bone py-2 shadow-[0_10px_40px_rgba(18,18,18,0.10)]">
          <div className="w-60">
            <Link
              to={`/shop?section=${group.section}`}
              data-testid={`nav-${group.id}-all`}
              onClick={() => setOpen(false)}
              className="block px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.15em] text-obsidian transition-colors hover:bg-linen"
            >
              All {group.label}
            </Link>
            <div className="my-1 border-t border-seam/60" />
            {group.subs.map((s) => (
              <Link
                key={s}
                to={`/shop?section=${group.section}&sub=${encodeURIComponent(s)}`}
                data-testid={`nav-${group.id}-${slugify(s)}`}
                onClick={() => setOpen(false)}
                className="block px-5 py-2 text-sm text-soot transition-colors hover:bg-linen hover:text-obsidian"
              >
                {s}
              </Link>
            ))}
          </div>
          {spotlight && (
            <div className="w-44 border-l border-seam/60 px-2">
              <Link
                to={`/product/${spotlight.id}`}
                data-testid={`nav-${group.id}-spotlight`}
                onClick={() => setOpen(false)}
                className="group/spot block"
              >
                <div className="overflow-hidden bg-linen">
                  {spotlight.images?.[0] && (
                    <img
                      src={spotlight.images[0]}
                      alt={spotlight.name}
                      loading="lazy"
                      className="aspect-[3/4] w-full object-cover transition-transform duration-500 group-hover/spot:scale-105"
                    />
                  )}
                </div>
                <p className="mt-2 px-1 text-[10px] font-bold uppercase tracking-[0.15em] text-soot">Featured</p>
                <p className="px-1 font-serif text-sm leading-tight">{spotlight.name}</p>
                <p className="px-1 pb-1 text-xs font-semibold">{inr(spotlight.price)}</p>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const POPULAR_SEARCHES = ["Kurti", "Dress", "Coord Sets", "Bedsheets", "Quilts", "Nightsuits"];

function SearchBar() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 60);
      return () => clearTimeout(t);
    }
    setQuery("");
    setResults([]);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const t = setTimeout(() => {
      api
        .get("/products", { params: { search: query.trim() } })
        .then((r) => setResults(r.data.slice(0, 8)))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const go = (id) => {
    setOpen(false);
    navigate(`/product/${id}`);
  };

  return (
    <>
      <button
        data-testid="search-button"
        aria-label="Search products"
        onClick={() => setOpen((o) => !o)}
        className="p-2 transition-colors hover:text-olive"
      >
        <Search className="h-5 w-5" />
      </button>
      {open && (
        <div
          data-testid="search-overlay"
          className="absolute inset-x-0 top-full border-b border-seam bg-bone/95 px-6 py-6 backdrop-blur-xl md:px-12"
        >
          <div className="mx-auto max-w-[1400px]">
            <div className="flex items-center gap-3 border-b border-obsidian pb-3">
              <Search className="h-5 w-5 shrink-0 text-soot" />
              <input
                ref={inputRef}
                data-testid="search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search kurtis, dresses, bedsheets…"
                className="w-full bg-transparent font-serif text-2xl font-light tracking-tight outline-none placeholder:text-soot/50"
              />
              <button
                data-testid="search-close-button"
                aria-label="Close search"
                onClick={() => setOpen(false)}
                className="p-1 text-soot transition-colors hover:text-obsidian"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            {query.trim().length < 2 && (
              <div className="mt-5" data-testid="popular-searches">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-soot">Popular Searches</p>
                <div className="mt-3 flex flex-wrap gap-2.5">
                  {POPULAR_SEARCHES.map((term) => (
                    <button
                      key={term}
                      data-testid={`popular-search-${slugify(term)}`}
                      onClick={() => setQuery(term)}
                      className="rounded-full border border-seam px-5 py-2.5 text-xs font-bold uppercase tracking-[0.12em] text-soot transition-colors duration-300 hover:border-obsidian hover:bg-obsidian hover:text-bone"
                    >
                      {term}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {query.trim().length >= 2 && (
              <div className="mt-5 max-h-[60vh] overflow-y-auto">
                {loading ? (
                  <p className="text-sm text-soot" data-testid="search-loading">Searching…</p>
                ) : results.length === 0 ? (
                  <p className="font-serif text-xl italic text-soot" data-testid="search-no-results">
                    No matches for &ldquo;{query}&rdquo; — try &ldquo;kurti&rdquo;, &ldquo;quilt&rdquo; or &ldquo;dress&rdquo;.
                  </p>
                ) : (
                  <div className="grid grid-cols-2 gap-x-4 gap-y-6 sm:grid-cols-4" data-testid="search-results">
                    {results.map((p) => (
                      <button key={p.id} data-testid={`search-result-${p.id}`} onClick={() => go(p.id)} className="group text-left">
                        <div className="aspect-[3/4] overflow-hidden bg-linen">
                          {p.images?.[0] && (
                            <img src={p.images[0]} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                          )}
                        </div>
                        <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.15em] text-soot">{p.subcategory}</p>
                        <p className="font-serif leading-tight">{p.name}</p>
                        <p className="text-xs font-semibold">{inr(p.price)}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default function Header() {
  const { user, logout } = useAuth();
  const { count, setIsOpen } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [featured, setFeatured] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const activeSection = location.pathname === "/shop" ? searchParams.get("section") : null;

  useEffect(() => {
    api
      .get("/products", { params: { featured: true } })
      .then((r) => setFeatured(r.data))
      .catch(() => {});
  }, []);

  const spotlights = {
    fashion: featured.find((p) => p.category === "fashion"),
    furnishing: featured.find((p) => p.category === "furnishing"),
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 border-b border-seam/50 bg-bone/80 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between px-6 md:px-12">
        <div className="flex items-center gap-4 lg:hidden">
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-button" aria-label="Open menu" className="p-1">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="overflow-y-auto bg-bone">
              <SheetTitle className="font-serif text-2xl">
                Hunar <span className="italic">Designs</span>
              </SheetTitle>
              <nav className="mt-10 flex flex-col gap-6">
                <Link
                  to="/"
                  data-testid="mobile-nav-home"
                  onClick={() => setMenuOpen(false)}
                  className="font-serif text-3xl font-light tracking-tight transition-colors hover:text-olive"
                >
                  Home
                </Link>
                {menuGroups.map((g) => (
                  <div key={g.id}>
                    <button
                      data-testid={`mobile-nav-${g.id}`}
                      onClick={() => setExpanded(expanded === g.id ? null : g.id)}
                      aria-expanded={expanded === g.id}
                      className="flex w-full items-center justify-between font-serif text-3xl font-light tracking-tight transition-colors hover:text-olive"
                    >
                      {g.label}
                      <ChevronDown className={`h-5 w-5 transition-transform duration-300 ${expanded === g.id ? "rotate-180" : ""}`} />
                    </button>
                    {expanded === g.id && (
                      <div className="mt-3 flex flex-col gap-2.5 border-l border-seam pl-4">
                        <Link
                          to={`/shop?section=${g.section}`}
                          data-testid={`mobile-nav-${g.id}-all`}
                          onClick={() => setMenuOpen(false)}
                          className="text-[11px] font-bold uppercase tracking-[0.15em] text-obsidian"
                        >
                          All {g.label}
                        </Link>
                        {g.subs.map((s) => (
                          <Link
                            key={s}
                            to={`/shop?section=${g.section}&sub=${encodeURIComponent(s)}`}
                            data-testid={`mobile-nav-${g.id}-${slugify(s)}`}
                            onClick={() => setMenuOpen(false)}
                            className="text-sm text-soot transition-colors hover:text-obsidian"
                          >
                            {s}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
                <Link
                  to="/mumkey"
                  data-testid="mobile-nav-mumkey"
                  onClick={() => setMenuOpen(false)}
                  className="font-serif text-3xl font-light italic tracking-tight text-[#B76E79] transition-colors hover:text-olive"
                >
                  Mumkey
                </Link>
                <Link
                  to="/about"
                  data-testid="mobile-nav-about"
                  onClick={() => setMenuOpen(false)}
                  className="font-serif text-3xl font-light tracking-tight transition-colors hover:text-olive"
                >
                  About Us
                </Link>
              </nav>
            </SheetContent>
          </Sheet>
        </div>

        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2.5 font-serif text-xl tracking-tight md:text-2xl">
          <img src="/brand/logo-hunar.png" alt="Hunar Designs" className="h-9 w-9 rounded-full object-cover ring-1 ring-seam md:h-10 md:w-10" />
          <span>Hunar <span className="italic font-light">Designs</span></span>
        </Link>

        <nav className="hidden items-center gap-10 lg:flex" data-testid="desktop-nav">
          <NavLink
            to="/"
            end
            data-testid="nav-home"
            className={({ isActive }) =>
              `text-xs font-bold uppercase tracking-[0.18em] transition-colors duration-300 hover:text-olive ${
                isActive ? "text-obsidian underline underline-offset-8" : "text-soot"
              }`
            }
          >
            Home
          </NavLink>
          {menuGroups.map((g) => (
            <NavDropdown key={g.id} group={g} active={activeSection === g.section} spotlight={spotlights[g.section]} />
          ))}
          <NavLink
            to="/mumkey"
            data-testid="nav-mumkey"
            className={({ isActive }) =>
              `text-xs font-bold uppercase tracking-[0.18em] transition-colors duration-300 hover:text-[#B76E79] ${
                isActive ? "text-[#B76E79] underline underline-offset-8" : "text-[#C98B93]"
              }`
            }
          >
            Mumkey
          </NavLink>
          <NavLink
            to="/about"
            data-testid="nav-about"
            className={({ isActive }) =>
              `text-xs font-bold uppercase tracking-[0.18em] transition-colors duration-300 hover:text-olive ${
                isActive ? "text-obsidian underline underline-offset-8" : "text-soot"
              }`
            }
          >
            About
          </NavLink>
        </nav>

        <div className="flex items-center gap-2 md:gap-4">
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="instagram-link"
            aria-label="Hunar Designs on Instagram"
            className="hidden p-2 transition-colors hover:text-olive sm:block"
          >
            <Instagram className="h-5 w-5" />
          </a>
          <SearchBar />
          <button
            data-testid="cart-button"
            aria-label="Open bag"
            onClick={() => setIsOpen(true)}
            className="relative p-2 transition-colors hover:text-olive"
          >
            <ShoppingBag className="h-5 w-5" />
            {count > 0 && (
              <span
                data-testid="cart-count-badge"
                className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-obsidian px-1 text-[10px] font-bold text-bone"
              >
                {count}
              </span>
            )}
          </button>
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="account-button" aria-label="Account" className="p-2 transition-colors hover:text-olive">
                  <User className="h-5 w-5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-bone">
                <DropdownMenuLabel>
                  <p className="font-serif text-base">{user.name}</p>
                  <p className="text-xs font-normal text-soot">{user.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {user.role === "admin" && (
                  <DropdownMenuItem data-testid="account-admin-link" onSelect={() => navigate("/admin")}>
                    Admin Panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid="logout-button" onSelect={logout}>
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
    </header>
  );
}
